import React, { useState, useEffect } from "react";
import { orderService } from "../../services/orderService";
import { useParams, useNavigate } from "react-router-dom";
import styles from "../../styles/Admin.module.css";

interface OrderFormState {
  clientName: string;
  clientEmail: string;
  carMake: string;
  carModel: string;
  carYear: string;
  vin: string;
  mileage: string;
  color: string;
  notes: string;
  status: "Pending" | "Shipping" | "Customs" | "Delivered";
  price: string;
  existingImages: string[];
  existingImageUrls: string[];
  images: File[];
}

const OrderForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState<OrderFormState>({
    clientName: "",
    clientEmail: "",
    carMake: "",
    carModel: "",
    carYear: "",
    vin: "",
    mileage: "",
    color: "",
    notes: "",
    status: "Pending",
    price: "",
    existingImages: [],
    existingImageUrls: [],
    images: [],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [newPreviews, setNewPreviews] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;
    const fetchOrder = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const order = await orderService.getById(id);

        if (!mounted) return;

        setForm({
          clientName: order.clientName || "",
          clientEmail: order.clientEmail || "",
          carMake: order.carMake || "",
          carModel: order.carModel || "",
          carYear: order.carYear || "",
          vin: order.vin || "",
          mileage: order.mileage || "",
          color: order.color || "",
          notes: order.notes || "",
          status: order.status || "Pending",
          price: order.price?.toString() || "",
          existingImages: order.images || [],
          existingImageUrls: order.imageUrls || [],
          images: [],
        });
      } catch (err) {
        console.error("Failed to load order", err);
        alert("Failed to load order data.");
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchOrder();
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    const urls = form.images.map((f) => URL.createObjectURL(f));
    setNewPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [form.images]);

  const handleChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;

    const newFiles: File[] = Array.from(fileList);

    setForm(prev => ({
      ...prev,
      images: [...prev.images, ...newFiles],
    }));

    e.target.value = "";
  };

  const handleRemoveExistingImage = (index: number) => {
    if (!window.confirm("Remove this image from the order?")) return;
    const updated = [...form.existingImages];
    updated.splice(index, 1);
    setForm({ ...form, existingImages: updated });
  };

  const handleRemoveNewImage = (index: number) => {
    const updated = [...form.images];
    updated.splice(index, 1);
    setForm({ ...form, images: updated });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const formData = new FormData();
      formData.append("clientName", form.clientName);
      formData.append("clientEmail", form.clientEmail);
      formData.append("carMake", form.carMake);
      formData.append("carModel", form.carModel);
      formData.append("carYear", form.carYear);
      formData.append("vin", form.vin);
      formData.append("mileage", form.mileage);
      formData.append("color", form.color);
      formData.append("notes", form.notes);
      formData.append("status", form.status);
      formData.append("price", form.price);

      form.images.forEach((file) => formData.append("images", file));
      form.existingImages.forEach((img) =>
        formData.append("existingImages[]", img)
      );

      if (id) {
        await orderService.update(id, formData);
        alert("Order updated successfully.");
      } else {
        await orderService.create(formData);
        alert("Order created successfully.");
      }

      navigate("/admin/orders");
    } catch (err) {
      console.error("Failed to save order", err);
      alert("Failed to save order.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    if (!window.confirm("Are you sure? This cannot be undone.")) {
      setShowDeleteModal(false);
      return;
    }

    setIsDeleting(true);
    try {
      await orderService.delete(id);
      alert("Order deleted.");
      navigate("/admin/orders");
    } catch (err) {
      console.error("Delete failed", err);
      alert("Failed to delete order.");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className={styles["admin-container"]}>
      <div className={`${styles["admin-container"]} ${styles["bg-white"]} ${styles["p-6"]} ${styles["rounded"]}`}>
        <h2 className={styles["admin-header"]}>
          {id ? "Edit Order" : "Create Order"}
        </h2>
      </div>

      <div className={`${styles["admin-container"]} ${styles["bg-white"]} ${styles["p-6"]} ${styles["rounded"]}`}>
        {isLoading ? (
          <p>Loading...</p>
        ) : (
          <form onSubmit={handleSubmit} className={styles["w-full"]}>
            <div className={styles.grid}>
              {/* Client Name */}
              <div className={styles.flexCol}>
                <label htmlFor="clientName">Client Name</label>
                <input
                  id="clientName"
                  type="text"
                  name="clientName"
                  value={form.clientName}
                  onChange={handleChange}
                  className={styles.inputField}
                  required
                />
              </div>

              {/* Client Email */}
              <div className={styles.flexCol}>
                <label htmlFor="clientEmail">Client Email</label>
                <input
                  id="clientEmail"
                  type="email"
                  name="clientEmail"
                  value={form.clientEmail}
                  onChange={handleChange}
                  className={styles.inputField}
                  required
                />
              </div>

              {/* Car Make */}
              <div className={styles.flexCol}>
                <label htmlFor="carMake">Car Make</label>
                <input
                  id="carMake"
                  type="text"
                  name="carMake"
                  value={form.carMake}
                  onChange={handleChange}
                  className={styles.inputField}
                  required
                />
              </div>

              {/* Car Model */}
              <div className={styles.flexCol}>
                <label htmlFor="carModel">Car Model</label>
                <input
                  id="carModel"
                  type="text"
                  name="carModel"
                  value={form.carModel}
                  onChange={handleChange}
                  className={styles.inputField}
                  required
                />
              </div>

              {/* Car Year */}
              <div className={styles.flexCol}>
                <label htmlFor="carYear">Year</label>
                <input
                  id="carYear"
                  type="text"
                  name="carYear"
                  value={form.carYear}
                  onChange={handleChange}
                  className={styles.inputField}
                />
              </div>

              {/* VIN */}
              <div className={styles.flexCol}>
                <label htmlFor="vin">VIN</label>
                <input
                  id="vin"
                  type="text"
                  name="vin"
                  value={form.vin}
                  onChange={handleChange}
                  className={styles.inputField}
                />
              </div>

              {/* Mileage */}
              <div className={styles.flexCol}>
                <label htmlFor="mileage">Mileage</label>
                <input
                  id="mileage"
                  type="text"
                  name="mileage"
                  value={form.mileage}
                  onChange={handleChange}
                  className={styles.inputField}
                />
              </div>

              {/* Color */}
              <div className={styles.flexCol}>
                <label htmlFor="color">Color</label>
                <input
                  id="color"
                  type="text"
                  name="color"
                  value={form.color}
                  onChange={handleChange}
                  className={styles.inputField}
                />
              </div>

              {/* Price */}
              <div className={styles.flexCol}>
                <label htmlFor="price">Price (UGX)</label>
                <input
                  id="price"
                  type="number"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  className={styles.inputField}
                />
              </div>

              {/* Status */}
              <div className={styles.flexCol}>
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className={styles.statusSelect}
                >
                  <option value="Pending">Pending</option>
                  <option value="Shipping">Shipping</option>
                  <option value="Customs">Customs</option>
                  <option value="Delivered">Delivered</option>
                </select>
              </div>

              {/* Notes */}
              <div className={styles.flexCol}>
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  className={styles.inputField}
                />
              </div>
            </div>

            {/* Existing Images */}
            <p className={styles["font-bold"]}>Existing Images</p>
            {form.existingImageUrls.length > 0 && (
              <div className={styles["flex-wrap"]}>
                {form.existingImageUrls.map((img, idx) => (
                  <div key={idx} className={styles["relative"]}>
                    <img src={img} className={`${styles.smallImage} ${styles.rounded}`} />
                    <button type="button" onClick={() => handleRemoveExistingImage(idx)}>×</button>
                  </div>
                ))}
              </div>
            )}

            {/* New Images */}
            {newPreviews.length > 0 && (
              <>
                <p className={`${styles["font-bold"]} ${styles["mt-2"]}`}>New Images</p>
                <div className={styles["flex-wrap"]}>
                  {newPreviews.map((url, idx) => (
                    <div key={idx} className={styles["relative"]}>
                      <img src={url} className={`${styles.smallImage} ${styles.rounded}`} />
                      <button type="button" onClick={() => handleRemoveNewImage(idx)}>×</button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Image Upload */}
            <div className={styles["mt-2"]}>
              <label htmlFor="images">Upload Images</label>
              <input
                id="images"
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className={styles.inputField}
              />
            </div>

            {/* Actions */}
            <div className={`${styles["mt-2"]} ${styles["flex"]} ${styles["justify-end"]} ${styles["gap-4"]}`}>
              <button type="submit" className={styles.adminAddButton} disabled={isSaving || isLoading}>
                {isSaving ? "Saving..." : id ? "Update Order" : "Create Order"}
              </button>

              {id && (
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  className={`${styles["bg-red-600"]} ${styles["p-3"]} ${styles["rounded"]}`}
                >
                  {isDeleting ? "Deleting..." : "Delete Order"}
                </button>
              )}
            </div>
          </form>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className={`${styles["fixed"]} ${styles["inset-0"]} ${styles["bg-black"]} ${styles["bg-opacity-40"]} ${styles["flex"]} ${styles["items-center"]} ${styles["justify-center"]}`}>
          <div className={`${styles["bg-white"]} ${styles["w-full"]} ${styles["max-w-sm"]} ${styles["rounded-lg"]} ${styles["p-6"]}`}>
            <h3 className={styles["font-bold"]}>Confirm Deletion</h3>
            <p className={styles["mt-2"]}>Are you sure you want to delete this order?</p>

            <div className={`${styles["flex"]} ${styles["justify-end"]} ${styles["gap-4"]} ${styles["mt-6"]}`}>
              <button onClick={() => setShowDeleteModal(false)} className={styles["border"]}>Cancel</button>
              <button onClick={handleDelete} className={`${styles["bg-red-600"]} ${styles["text-white"]}`}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderForm;
